import { getFocusableElements } from '@theme/a11y';
import { classes, isFromBreakpoint } from '@theme/helpers';
import { HTMLCustomElement } from '@theme/html-custom-element';

class CurrencySelector extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    if (!this.refs.panel) return;

    this.on('click', this.refs.trigger, this.handleTriggerClick.bind(this));
    this.on('click', this.refs.option.all, this.handleOptionClick.bind(this));
    this.on('keyup', this, this.handleKeyUp.bind(this));
    this.on('click', document, this.handleDocumentClick.bind(this));
    this.on('focusout', this, this.handleFocusOut.bind(this));
    this.on('resize', window, this.alignListWithTrigger.bind(this), { passive: true });
  }

  get isOpen() {
    return this.classList.contains(classes.open);
  }

  /**
   * Aligns the currency list with the trigger, matching the navigation submenu.
   */
  alignListWithTrigger() {
    if (!this.refs.list || !this.refs.trigger) return;

    const triggerRect = this.refs.trigger.getBoundingClientRect();

    if (isFromBreakpoint('medium')) {
      this.refs.list.style.paddingInlineStart = `${triggerRect.left}px`;
      this.refs.list.style.paddingInlineEnd = '';
    } else {
      this.refs.list.style.paddingInlineStart = '';
      this.refs.list.style.paddingInlineEnd = '';
    }
  }

  /**
   * @param {boolean} isOpen
   */
  setOptionsTabIndex(isOpen) {
    for (const option of this.refs.option.all) {
      option.tabIndex = isOpen ? 0 : -1;
    }
  }

  /**
   * @param {MouseEvent} event
   */
  handleTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isOpen) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  /**
   * @param {MouseEvent} event
   */
  handleOptionClick(event) {
    event.preventDefault();

    const option = event.currentTarget;
    const countryCode = option.dataset.countryCode;

    if (!countryCode || countryCode === this.refs.countryInput.value) {
      this.closePanel();
      return;
    }

    this.refs.countryInput.value = countryCode;
    this.querySelector('form')?.requestSubmit();
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeyUp(event) {
    if (event.code !== 'Escape' || !this.isOpen) return;

    event.preventDefault();
    this.closePanel();
    this.refs.trigger.focus();
  }

  /**
   * @param {MouseEvent} event
   */
  handleDocumentClick(event) {
    if (!this.isOpen || this.contains(event.target)) return;

    this.closePanel();
  }

  /**
   * @param {FocusEvent} event
   */
  handleFocusOut(event) {
    const nextTarget = event.relatedTarget;

    if (!nextTarget || !this.contains(nextTarget)) {
      this.closePanel();
    }
  }

  openPanel() {
    this.alignListWithTrigger();
    this.classList.add(classes.open);
    this.refs.panel.classList.add(classes.open);
    this.refs.panel.setAttribute('aria-hidden', 'false');
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    this.setOptionsTabIndex(true);

    for (const focusable of getFocusableElements(this.refs.panel)) {
      focusable.tabIndex = 0;
    }
  }

  closePanel() {
    if (!this.refs.panel) return;

    this.classList.remove(classes.open);
    this.refs.panel.classList.remove(classes.open);
    this.refs.panel.setAttribute('aria-hidden', 'true');
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    this.setOptionsTabIndex(false);

    for (const focusable of getFocusableElements(this.refs.panel)) {
      focusable.tabIndex = -1;
    }
  }
}

if (!customElements.get('currency-selector')) {
  customElements.define('currency-selector', CurrencySelector);
}
